import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6bgb3uqd308q1a0914g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/85 backdrop-blur-md shadow-sm'
            : 'bg-white/85 backdrop-blur-md'
        }`}
        style={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}
      >
        <div className="px-[60px] h-[80px] flex items-center">
          <img 
            src="https://public.readdy.ai/ai/img_res/8476e800-b4c6-4104-bed3-3214dd78552f.png" 
            alt="FRENDY d.o.o. Logo" 
            className="h-12 w-auto object-contain"
          />
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-[120px] pb-[120px] px-[140px]">
        <div className="flex items-center gap-[60px] min-h-[85vh]">
          <div className="w-[45%]">
            <div className="rounded-[32px] overflow-hidden h-[600px] shadow-lg">
              <img
                src="https://public.readdy.ai/ai/img_res/2d5034511f6ab89e78b7a7c9207065c2.jpg"
                alt="Modern iOS Development"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="w-[55%] pl-[80px]">
            <h1 className="text-[48px] leading-[1.3] font-serif text-[#2D2D2D] mb-[60px] max-w-[520px]">
              A team of experts developing modern applications for the iOS platform
            </h1>
            <p className="text-[18px] leading-[1.8] text-[#6B6B6B] max-w-[520px]">
              Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine innovation with reliability to deliver exceptional iOS solutions.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-[120px] px-[140px]">
        <div className="flex justify-center">
          <div
            className="bg-white rounded-[28px] shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-[80px] w-[80%]"
            style={{ transform: 'rotate(-2deg)' }}
          >
            <div style={{ transform: 'rotate(2deg)' }}>
              <div className="flex gap-[80px]">
                <div className="w-[40%]">
                  <div className="text-[#A8E6CF] text-[120px] leading-none mb-[40px] font-serif italic">
                    "
                  </div>
                  <p className="text-[32px] leading-[1.6] font-serif italic text-[#1A1A1A]">
                    Crafting excellence in mobile innovation
                  </p>
                </div>
                <div className="w-[60%]">
                  <p className="text-[16px] leading-[1.9] text-[#4A4A4A] mb-[32px]">
                    FRENDY d.o.o. is dedicated to pushing the boundaries of what's possible on the iOS platform. Our team combines technical expertise with creative vision to develop applications that not only meet but exceed user expectations.
                  </p>
                  <div className="h-[2px] w-[60px] bg-[#A8E6CF] mb-[32px]"></div>
                  <p className="text-[16px] leading-[1.9] text-[#4A4A4A] mb-[32px]">
                    We specialize in creating intuitive, powerful tools that enhance device performance and protect user privacy. Every product we develop reflects our commitment to quality, security, and user satisfaction.
                  </p>
                  <div className="h-[2px] w-[60px] bg-[#A8E6CF] mb-[32px]"></div>
                  <p className="text-[16px] leading-[1.9] text-[#4A4A4A]">
                    Our approach combines cutting-edge technology with user-centered design principles, ensuring that each application delivers real value and exceptional performance to iOS users worldwide.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-[120px] px-[140px]">
        <div className="mb-[80px]">
          <p className="text-[24px] text-[#9B9B9B] mb-[12px]">Our Products</p>
          <h2 className="text-[44px] font-serif italic text-[#1A1A1A]">
            Innovative iOS Solutions
          </h2>
        </div>
        <div className="grid grid-cols-3 gap-[32px]">
          {/* VPN Card */}
          <div className="aspect-square rounded-[24px] bg-[#1B2B44] relative overflow-hidden group cursor-pointer transition-transform duration-300 hover:scale-[1.02]">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20network%20security%20visualization%20with%20geometric%20shield%20icon%20and%20flowing%20data%20streams%20in%20deep%20navy%20blue%20background%20with%20electric%20cyan%20glowing%20accents%20representing%20VPN%20protection%20and%20encrypted%20connections%20with%20modern%20minimalist%20design&width=600&height=600&seq=frendy-vpn-001&orientation=squarish"
                alt="VPN Security"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-[40px]">
              <div className="w-[80px] h-[80px] flex items-center justify-center mb-[32px]">
                <i className="ri-shield-check-line text-[64px] text-white"></i>
              </div>
              <h3 className="text-[36px] font-bold text-white mb-[16px]">VPN</h3>
              <div className="bg-[#A8E6CF] rounded-full px-[20px] py-[8px]">
                <span className="text-[14px] font-medium text-[#1A1A1A]">Security Solution</span>
              </div>
            </div>
          </div>

          {/* Cleaner Card */}
          <div className="aspect-square rounded-[24px] bg-gradient-to-br from-[#FF6B6B] to-[#FFA07A] relative overflow-hidden group cursor-pointer transition-transform duration-300 hover:scale-[1.02]">
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-[40px]">
              <div className="w-[80px] h-[80px] flex items-center justify-center mb-[32px]">
                <i className="ri-brush-line text-[64px] text-white"></i>
              </div>
              <h3 className="text-[36px] font-bold text-white mb-[16px]">Cleaner</h3>
              <div className="bg-[#A8E6CF] rounded-full px-[20px] py-[8px]">
                <span className="text-[14px] font-medium text-[#1A1A1A]">Performance Tool</span>
              </div>
            </div>
          </div>

          {/* Coming Soon Card */}
          <div className="aspect-square rounded-[24px] relative overflow-hidden group cursor-pointer transition-transform duration-300 hover:scale-[1.02]">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=sophisticated%20iOS%20device%20interface%20mockup%20showing%20sleek%20modern%20smartphone%20screen%20with%20elegant%20app%20design%20in%20grayscale%20tones%20featuring%20minimalist%20user%20interface%20and%20professional%20technology%20aesthetic%20with%20clean%20lines%20and%20contemporary%20style&width=600&height=600&seq=frendy-future-001&orientation=squarish"
                alt="Coming Soon"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-[40px]">
              <h3 className="text-[36px] font-bold text-white mb-[16px]">Coming Soon</h3>
              <div className="bg-[#A8E6CF] rounded-full px-[20px] py-[8px]">
                <span className="text-[14px] font-medium text-[#1A1A1A]">Innovation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-[120px] px-[140px]">
        <div className="bg-[#2A2A2A] rounded-[32px] py-[100px] px-[80px] w-[90%] mx-auto">
          <div className="flex gap-[80px]">
            <div className="w-[35%]">
              <h2 className="text-[52px] font-bold text-white mb-[40px]">Get in Touch</h2>
              <p className="text-[16px] text-white/80 leading-[1.8] mb-[60px]">
                We're here to answer your questions and discuss how our iOS solutions can benefit you. Reach out to our team today.
              </p>
              <svg width="120" height="4" viewBox="0 0 120 4" fill="none">
                <path d="M0 2C40 2 80 2 120 2" stroke="#A8E6CF" strokeWidth="2" />
              </svg>
            </div>
            <div className="w-[65%]">
              <div className="grid grid-cols-2 gap-x-[60px] gap-y-[32px]">
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">Address</p>
                      <p className="text-[16px] text-white">Obala Pape Ivana Pavla II. 16a</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">Email</p>
                      <p className="text-[16px] text-white">help@frendydoo.com</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">City</p>
                      <p className="text-[16px] text-white">Dubrovnik</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">Phone</p>
                      <p className="text-[16px] text-white">+1 6622009825</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-send-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">Zip</p>
                      <p className="text-[16px] text-white">20000</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start gap-[16px]">
                    <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-[20px] text-[#A8E6CF]"></i>
                    </div>
                    <div>
                      <p className="text-[12px] uppercase tracking-wider text-[#A8E6CF] mb-[8px]">Country</p>
                      <p className="text-[16px] text-white">Croatia</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form 
                onSubmit={handleSubmit} 
                className="mt-[60px] space-y-[24px]"
                data-readdy-form
                id="contact-form"
              >
                <div className="grid grid-cols-2 gap-[24px]">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                      className="w-full px-[20px] py-[14px] bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:border-[#A8E6CF] transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      required
                      className="w-full px-[20px] py-[14px] bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:border-[#A8E6CF] transition-colors text-sm"
                    />
                  </div>
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-[20px] py-[14px] bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:border-[#A8E6CF] transition-colors resize-none text-sm"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="px-[32px] py-[14px] bg-[#A8E6CF] text-[#1A1A1A] rounded-lg font-medium hover:bg-[#8FD4B8] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-[80px] px-[140px]">
        <div className="flex flex-col items-center">
          <div className="w-[60%] h-[1px] bg-white/10 mb-[40px]"></div>
          <p className="text-[120px] font-bold text-white/[0.03] select-none">FRENDY d.o.o.</p>
          <div className="mt-[40px]">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[14px] text-[#9B9B9B] hover:text-[#A8E6CF] transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
